import { Extension } from "@tiptap/core";
import { NodeSelection, Plugin, PluginKey } from "@tiptap/pm/state";
import { Mention } from "@tiptap/extension-mention";
import { mergeAttributes, ReactNodeViewRenderer } from "@tiptap/react";
import MentionNode from "./MentionNode.tsx";
import { linkColor } from "./MentionInput.tsx";
import Image from "@tiptap/extension-image";

export const NoNewLine = (multiline?: boolean) => {
    return Extension.create({
        name: "no_new_line",

        addProseMirrorPlugins() {
            return [
                new Plugin({
                    key: new PluginKey("eventHandler"),
                    props: {
                        handleKeyDown: (view, event) => {
                            if (event.key === "Enter" && !event.shiftKey && !multiline) {
                                // True prevents creating a new line when enter is presed
                                return true;
                            }
                        },
                        // … and many, many more.
                        // Here is the full list: https://prosemirror.net/docs/ref/#view.EditorProps
                    },
                }),
            ];
        },
    });
};

export const CustomMention = (extensionName: string, linkColor?: linkColor) =>
    Mention.extend({
        name: `mention${extensionName}`,
        addAttributes() {
            return {
                ...this.parent?.(),
                label: {
                    default: null,
                    rendered: false,
                    parseHTML: (element) => element.getAttribute("data-label"),
                },
                entityType: {
                    default: null, // Define custom attribute
                    parseHTML: (element) => element.getAttribute("data-entity-type"),
                    renderHTML: (attributes) => ({
                        "data-entity-type": attributes.entityType || "",
                    }),
                },
                parentId: {
                    default: null, // Define custom attribute
                    rendered: false,
                    parseHTML: (element) => element.getAttribute("data-parent-id"),
                },
                linkColor: {
                    default: linkColor,
                    rendered: false,
                },
            };
        },
        addNodeView() {
            return ReactNodeViewRenderer(MentionNode);
        },
        parseHTML() {
            return [
                {
                    tag: "mention-component",
                },
            ];
        },
        renderHTML({ HTMLAttributes }) {
            return ["mention-component", mergeAttributes(HTMLAttributes)];
        },
    });

export const ResizableImage = Image.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            width: {
                default: null,
                parseHTML: (element) => {
                    const width = element.getAttribute("width") || element.style.width;
                    return width ? width.replace("px", "") : null;
                },
                renderHTML: () => ({}),
            },
            height: {
                default: null,
                parseHTML: (element) => {
                    const height = element.getAttribute("height") || element.style.height;
                    return height ? height.replace("px", "") : null;
                },
                renderHTML: () => ({}),
            },
            align: {
                default: "left",
                parseHTML: (element) => element.getAttribute("data-align") || "left",
                renderHTML: (attributes) => {
                    const align = attributes.align || "left";
                    const styleParts: string[] = ["display:block"];

                    styleParts.push("max-width:100%");
                    if (attributes.width) {
                        styleParts.push(`width:${attributes.width}px`);
                    }
                    if (attributes.height) {
                        styleParts.push(`height:${attributes.height}px`);
                    }
                    if (align === "center") {
                        styleParts.push("margin-left:auto", "margin-right:auto");
                    } else if (align === "right") {
                        styleParts.push("margin-left:auto", "margin-right:0");
                    }

                    return {
                        "data-align": align,
                        style: styleParts.join(";"),
                    };
                },
            },
        };
    },
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey("imageProtection"),
                props: {
                    handleKeyDown: (view) => {
                        const { selection } = view.state;
                        if (selection instanceof NodeSelection && selection.node.type.name === "image") {
                            return true;
                        }
                        return false;
                    },
                    handleTextInput: (view) => {
                        const { selection } = view.state;
                        if (selection instanceof NodeSelection && selection.node.type.name === "image") {
                            return true;
                        }
                        return false;
                    },
                },
            }),
        ];
    },
});
